/*
 * Top level module for the quick create interface, which is an alternate UI
 * for creating topics.
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "underscore",
  "configuration/properties",
  "messaging/messenger",
  "modules/quickCreate/quickCreateView",
  "modules/shared/sortableList/sortableListModule",
  "modules/quickCreate/quickTopicModal/quickTopicModalModule",
  "modules/quickCreate/quickCreateControls/quickCreateControlsModule",
  "modules/quickCreate/quickResponseModal/quickResponseModalModule",
  "modules/quickCreate/quickContentModal/quickContentModalModule",
  "modules/quickCreate/quickExampleModal/quickExampleModalModule",
  "modules/header/kbMenu/kbMenuModel",
  "modules/quickCreate/quickCreateModel",
], function (
  Marionette,
  Backbone,
  $,
  _,
  Properties,
  Messenger,
  QuickCreateView,
  SortableListModule,
  QuickTopicModalModule,
  QuickCreateControlsModule,
  QuickResponseModalModule,
  QuickContentModalModule,
  QuickExampleModalModule,
  KbMenuModel,
) {
  const QuickCreateController = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "quickCreate";
      this.region = options.region;

      this.router = new Router({
        controller: this,
      });

      this._setUpVents();
    },
    show: function () {
      //set URL
      const kbId = Messenger.reqres.request("active:kb");
      Backbone.history.navigate(kbId + "/quickcreate");
      document.title = $.t("quickCreate.title") + " - " + Properties.applicationName;
      //get a list of quick create topics
      const quickCreateTopics = Messenger.reqres.request("quick:create:topics");

      Messenger.onSave(function () {
        console.log("saving quick create");
        quickCreateTopics.saveCollection();
      }, this);

      this.layout = new QuickCreateView.quickCreateLayout();

      this.layout.on(
        "add:topic",
        function () {
          const newTopic = quickCreateTopics.addTopic();
          //open modal to create the name for the first time
          Messenger.vent.trigger("show:modal", QuickTopicModalModule, {}, newTopic, {
            title: "topics.tooltip.addTopic",
            modalWidth: 400,
            closeButton: false,
            closeOnBackgroundClick: false,
            trackInteraction: {
              action: "Add Quick Topic",
              object: "Topic",
            },
          });
        },
        this,
      );

      this.layout.on(
        "show:dashboard",
        function () {
          this._reconfigureDefaultHomePage();
          //switch to the knowledge dashboard
          Messenger.vent.trigger("show:dashboard");
        },
        this,
      );

      //we need to know whether the KB has response versioning, so wait until the KB model is loaded before proceeding
      const kbModel = Messenger.reqres.request("active:kb:model");
      const headers = Messenger.reqres.request("quick:create:headers", kbModel);
      //hash of events for when a user clicks on a component button
      const componentEvents = this._sortableListEvents;
      this.list = this.addSubModule(
        new SortableListModule({
          region: this.layout.quickCreateList,
          headers: headers,
          recordsPerPage: SortableListModule.DEFAULT_RECORDS_PER_PAGE,
          namespace: this.name,
          extendedEvents: componentEvents,
        }),
      );
      this.controls = this.addSubModule(
        new QuickCreateControlsModule({ region: this.layout.quickCreateControls }),
      );
      this.layout.on(
        "show",
        function () {
          kbModel.onSynced(function () {
            this.list.show(quickCreateTopics);
            this.controls.show();
          }, this);
        },
        this,
      );
      this.showRegion(this.layout);
    },
    _reconfigureDefaultHomePage: function () {
      //get the users model and change the 'showQuickCreate' attribute to false for the active KB
      const user = Messenger.reqres.request("user");
      const kbId = Messenger.reqres.request("active:kb");
      user.toggleQuickCreateAsHomePage(kbId, false); //set the knowledge dashboard to load by default
      //save the user model, but don't wait up for a response or error.  Missing this change is not
      //as problematic as making the UI wait for the request to be processed.
      user.saveModel({
        success: _.noop,
        error: _.noop,
      });
    },
    _route: function (kbId) {
      const isAuthor = Messenger.reqres.request("user:is:author", kbId);
      if (!isAuthor) {
        Backbone.history.navigate("", { trigger: true });
        return;
      }
      Messenger.command.execute("set:active:kb", kbId);
      Messenger.command.execute("change:menu", null);
      this.show();
    },
    _sortableListEvents: {
      "click .response-component": function () {
        const topic = this.model; //we execute this function in the context of the sortableList's itemView
        //bundle conversation plus the quick create model
        const modalBundle = {
          node: topic.conversation(),
          quickCreate: topic.quickCreate(),
          topic: topic,
        };
        //re-render row when modal is closed
        topic.quickCreate().once(
          "change",
          function () {
            this.render();
          },
          this,
        );
        //trigger showing the response editor modal
        Messenger.vent.trigger("show:modal", QuickResponseModalModule, {}, modalBundle, {
          title: "topic.responseEditor.title",
          closeButton: false,
          closeOnBackgroundClick: false,
          modalHeight: "690px",
          modalWidth: "1000px",
          trackInteraction: {
            action: "Edit Quick Topic Response",
            id: topic.id,
            idType: "Topic",
            object: "Topic",
          },
        });
      },
      "click .content-component": function () {
        const topic = this.model; //we execute this function in the context of the sortableList's itemView
        //bundle up the conversation and the quickCreate model to send to the modal
        const modalBundle = {
          node: topic.conversation(),
          quickCreate: topic.quickCreate(),
        };
        //re-render row when modal is closed
        topic.quickCreate().once(
          "change",
          function () {
            this.render();
          },
          this,
        );
        //trigger showing the content editor modal
        Messenger.vent.trigger("show:modal", QuickContentModalModule, {}, modalBundle, {
          title: "topic.responseProperties.associatedContent",
          closeButton: false,
          closeOnBackgroundClick: false,
          trackInteraction: {
            action: "Edit Quick Topic Content",
            id: topic.id,
            idType: "Topic",
            object: "Topic",
          },
        });
      },
      "click .example-component": function () {
        const topic = this.model;
        //re-render row when modal is closed
        topic.quickCreate().once(
          "change",
          function () {
            this.render();
          },
          this,
        );
        //trigger showing the example editor modal
        Messenger.vent.trigger("show:modal", QuickExampleModalModule, {}, topic, {
          title: "topic.example.title",
          closeButton: false,
          closeOnBackgroundClick: false,
          trackInteraction: {
            action: "Edit Quick Topic Example",
            id: topic.id,
            idType: "Topic",
            object: "Topic",
          },
        });
      },
      "click .quick-create-publish": function () {
        const topic = this.model;
        let message;
        let action;
        let newStatusId;
        //decide on message based on response versioning
        if (topic.get("statusId") == KbMenuModel.statuses.NON_VERSIONED_CREATED) {
          message = "quickCreate.enableMessage";
          action = "Enable Topic";
          newStatusId = KbMenuModel.statuses.NON_VERSIONED_ENABLED;
        } else {
          message = "quickCreate.publishMessage";
          action = "Publish Topic";
          newStatusId = KbMenuModel.statuses.VERSIONED_PUBLISHED;
        }
        Messenger.yesNoDialog(
          message,
          {
            yes: function () {
              Messenger.vent.trigger("track:interaction", {
                action: action,
                id: topic.id,
                idType: "Topic",
                object: "Topic",
              });
              //the user just clicked the publish/enable button.  So let's publish/enable their topic, save it, and remove it from the list.
              //The status id for 'created' is one less than the status Ids for 'enabled' and 'published'
              //(obviously, the 'created' status ID differs based on whether response versioning exists)
              //So just increment the status ID by one.  The topic model will take of publishing the conversation.
              topic.set("statusId", newStatusId);
              //show a saving display while the topic is saving
              Messenger.vent.trigger("show:saving:display");
              if (newStatusId == KbMenuModel.statuses.NON_VERSIONED_ENABLED) {
                const conversation = topic.conversation();
                conversation.enableAll = true;
                conversation.needToEnable = true;
              } else {
                const liveConversation = topic.liveConversation(false);
                liveConversation.publishAll = true;
                liveConversation.needToPublish = true;
              }
              //save the topic and remove the topic from the collection when completed
              topic.saveModel({
                success: function () {
                  topic.collection.remove(topic);
                  Messenger.vent.trigger("close:saving:display");
                },
                error: function (_model, response) {
                  Messenger.vent.trigger("close:saving:display");
                  Messenger.showErrorFromResponse(response);
                },
              });
            },
          },
          this,
        );
      },
    },
    _setUpVents: function () {
      //trigger showing the quick create page
      Messenger.vent.on(
        "show:quick:create",
        function () {
          this.show();
        },
        this,
      );
    },
  });

  const Router = Marionette.AppRouter.extend({
    appRoutes: {
      ":kbId/quickcreate": "_route",
    },
  });

  return QuickCreateController;
});
